import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { auth, db, storage } from '../firebase';
import ImageModal from './ImageModal';
import { 
  collection, 
  addDoc, 
  query, 
  where, 
  orderBy, 
  onSnapshot, 
  serverTimestamp,
  doc,
  getDoc,
  updateDoc,
  deleteDoc
} from 'firebase/firestore';
import { 
  ref, 
  uploadBytes, 
  getDownloadURL, 
  deleteObject 
} from 'firebase/storage';
import { onAuthStateChanged } from 'firebase/auth';
import { calculateUserBadges, UserBadges } from '../utils/badgeCalculator';

// Constants
const MOBILE_BREAKPOINT = 768;
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const MAX_IMAGES_PER_REVIEW = 5;
const ERROR_DISPLAY_TIMEOUT = 5000;
const LONG_ERROR_DISPLAY_TIMEOUT = 10000;
const SUBMISSION_TIMEOUT = 30000;
const IMAGE_DIMENSIONS = {
  mobile: 275,
  desktop: 400
};

const CourseReviews = ({ courseId, courseName }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewText, setReviewText] = useState('');
  const [rating, setRating] = useState(null);
  const [hoverRating, setHoverRating] = useState(null);
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedImages, setSelectedImages] = useState([]);
  const [editingReview, setEditingReview] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [courses, setCourses] = useState([]);
  const [userBadges, setUserBadges] = useState({});
  const [userExistingReview, setUserExistingReview] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const fileInputRef = useRef(null);
  const imagePreviewUrlsRef = useRef(new Set());

  // Available review tags
  const availableTags = [
    { id: 'pace', label: 'Pace of Play' },
    { id: 'conditions', label: 'Conditions' },
    { id: 'layout', label: 'Layout' },
    { id: 'challenge', label: 'Challenge' }
  ];

  // Cleanup function for object URLs to prevent memory leaks
  const cleanupObjectUrls = useCallback(() => {
    imagePreviewUrlsRef.current.forEach(url => {
      URL.revokeObjectURL(url);
    });
    imagePreviewUrlsRef.current.clear();
  }, []);

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      cleanupObjectUrls();
    };
  }, [cleanupObjectUrls]);


  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        
        // Fetch user profile from custom users collection
        const fetchUserProfile = async () => {
          try {
            const userDoc = await getDoc(doc(db, 'users', user.uid));
            if (userDoc.exists()) {
              const profileData = userDoc.data();
              setUserProfile(profileData);
            }
          } catch (error) {
            console.error('Error fetching user profile:', error);
          }
        };
        
        fetchUserProfile();
      } else {
        setUserProfile(null);
      }
      setUser(user);
    });
    return () => unsubscribe();
  }, []);

  // Fetch courses data for badge calculations
  useEffect(() => {
    fetch(`${process.env.PUBLIC_URL}/courses.json`)
      .then(res => res.json())
      .then(data => setCourses(data))
      .catch(error => console.error('Error fetching courses:', error));
  }, []);

  useEffect(() => {
    if (!courseId) return;


    const q = query(
      collection(db, 'reviews'),
      where('courseId', '==', courseId),
      orderBy('createdAt', 'desc')
    );

          const unsubscribe = onSnapshot(q, async (snapshot) => {
        const reviewsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setReviews(reviewsData);

        // Check if current user has already reviewed this course
        if (user && user.uid) {
          const existingReview = reviewsData.find(review => review.userId === user.uid);
          setUserExistingReview(existingReview || null);
        }

        // Calculate badges for all reviewers
        if (courses.length > 0) {
          const badgesMap = {};
          for (const review of reviewsData) {
            if (review.userId && !badgesMap[review.userId]) {
              try {
                const userDoc = await getDoc(doc(db, 'users', review.userId));
                if (userDoc.exists()) {
                  const userData = userDoc.data();
                  const badges = calculateUserBadges(userData.playedCourses || [], courses);
                  badgesMap[review.userId] = badges;
                }
              } catch (error) {
                console.error('Error fetching user data for badges:', error);
              }
            }
          }
          setUserBadges(badgesMap);
        }
      }, (error) => {
        console.error('Error fetching reviews:', error);
      });

    return () => unsubscribe();
  }, [courseId, courseName, courses]);

  const handleImageSelect = useCallback((e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(file => 
      file.type.startsWith('image/') && file.size <= MAX_FILE_SIZE
    );
    
    if (validFiles.length + selectedImages.length > MAX_IMAGES_PER_REVIEW) {
      setError(`You can upload a maximum of ${MAX_IMAGES_PER_REVIEW} images per review.`);
      setTimeout(() => setError(null), ERROR_DISPLAY_TIMEOUT);
      return;
    }

    // Clean up old URLs before adding new ones
    cleanupObjectUrls();
    
    setSelectedImages(prev => [...prev, ...validFiles]);
    
    // Clear the input to allow selecting the same file again
    e.target.value = '';
  }, [selectedImages.length, cleanupObjectUrls]);

  const removeImage = useCallback((index) => {
    setSelectedImages(prev => {
      const newImages = prev.filter((_, i) => i !== index);
      // Clean up URLs when images are removed
      cleanupObjectUrls();
      return newImages;
    });
  }, [cleanupObjectUrls]);

  // Lightweight image preview with minimal memory usage
  const ImagePreview = ({ file, index, onRemove }) => {
    const [showPreview, setShowPreview] = useState(false);
    const [objectUrl, setObjectUrl] = useState(null);
    
    // Only create object URL when user wants to see preview
    const handlePreviewClick = () => {
      if (!objectUrl) {
        const url = URL.createObjectURL(file);
        setObjectUrl(url);
        imagePreviewUrlsRef.current.add(url);
      }
      setShowPreview(!showPreview);
    };
    
    // Cleanup on unmount
    useEffect(() => {
      return () => {
        if (objectUrl) {
          URL.revokeObjectURL(objectUrl);
          imagePreviewUrlsRef.current.delete(objectUrl);
        }
      };
    }, [objectUrl]);
    
    return (
      <div style={{ position: 'relative' }}>
        <div
          style={{
            width: 80,
            height: 80,
            backgroundColor: '#f3f4f6',
            borderRadius: 8,
            border: '1px solid #d1d5db',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '8px',
            color: '#6b7280',
            textAlign: 'center',
            padding: '4px',
            cursor: 'pointer'
          }}
          onClick={handlePreviewClick}
        >
          {showPreview && objectUrl ? (
            <img
              src={objectUrl}
              alt={`Preview ${index + 1}`}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                borderRadius: 4
              }}
            />
          ) : (
            <>
              <div style={{ fontSize: '20px', marginBottom: '2px' }}>📷</div>
              <div>{file.name.length > 8 ? file.name.substring(0, 8) + '...' : file.name}</div>
            </>
          )}
        </div>
        <button
          type="button"
          onClick={() => onRemove(index)}
          style={{
            position: 'absolute',
            top: -8,
            right: -8,
            background: '#ef4444',
            color: 'white',
            border: 'none',
            borderRadius: '50%',
            width: 20,
            height: 20,
            cursor: 'pointer',
            fontSize: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          ×
        </button>
      </div>
    );
  };


  const uploadImages = async () => {
    if (selectedImages.length === 0) return [];

    const uploadPromises = selectedImages.map(async (file) => {
      const fileName = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}_${file.name}`;
      const storageRef = ref(storage, `reviews/${courseId}/${user.uid}/${fileName}`);
      
      try {
        const snapshot = await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(snapshot.ref);
        return {
          url: downloadURL,
          path: snapshot.ref.fullPath,
          name: file.name
        };
      } catch (error) {
        console.error('Error uploading image:', error);
        setError('Error uploading image. Please try again.');
        setTimeout(() => setError(null), 5000);
        throw error;
      }
    });

    return Promise.all(uploadPromises);
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!user) return;
    if (!rating) {
      setError('Please select a rating before submitting.');
      setTimeout(() => setError(null), ERROR_DISPLAY_TIMEOUT);
      return;
    }

    // Prevent duplicate reviews - check both state and current reviews array
    const hasExistingReview = userExistingReview || reviews.find(review => review.userId === user.uid);
    if (hasExistingReview && !editingReview) {
      setError('You have already reviewed this course. You can edit your existing review instead.');
      setTimeout(() => setError(null), ERROR_DISPLAY_TIMEOUT);
      return;
    }

    setSubmitting(true);
    
    // Add timeout to prevent indefinite hanging
    const timeoutId = setTimeout(() => {
      setSubmitting(false);
      setError('Submission timed out. Please try again.');
      setTimeout(() => setError(null), LONG_ERROR_DISPLAY_TIMEOUT);
    }, SUBMISSION_TIMEOUT);

    try {
      let imageUrls = [];
      
      // Try to upload images, but don't fail the entire submission if it fails
      if (selectedImages.length > 0) {
        try {
          imageUrls = await uploadImages();
        } catch (uploadError) {
          console.warn('Image upload failed, continuing without images:', uploadError);
          // Continue with review submission without images
          setError('Images could not be uploaded, but your review will be submitted. Please try uploading images later.');
          setTimeout(() => setError(null), LONG_ERROR_DISPLAY_TIMEOUT);
        }
      }

             const reviewData = {
         courseId,
         courseName,
         userId: user.uid,
         userEmail: user.email,
         displayName: userProfile?.displayName || userProfile?.username || user.displayName || user.email?.split('@')[0] || 'Anonymous User',
         username: userProfile?.username || null,
         rating,
         tags: selectedTags,
         text: reviewText,
         images: imageUrls,
         createdAt: serverTimestamp(),
         updatedAt: serverTimestamp()
       };


             const docRef = await addDoc(collection(db, 'reviews'), reviewData);

       // Update the userExistingReview state immediately to prevent duplicate submissions
       const newReview = {
         id: docRef.id,
         ...reviewData
       };
       setUserExistingReview(newReview);

       // Reset form
       setReviewText('');
       setRating(null);
       setSelectedTags([]);
       setSelectedImages([]);
       setShowReviewForm(false);
       setEditingReview(null);
       
       // Clean up object URLs
       cleanupObjectUrls();
       
       // Clear timeout since submission succeeded
       clearTimeout(timeoutId);
    } catch (error) {
      console.error('Error submitting review:', error);
      let errorMessage = 'Error submitting review. Please try again.';
      
      // More specific error messages
      if (error.code === 'permission-denied') {
        errorMessage = 'Permission denied. Please check your authentication.';
      } else if (error.code === 'unavailable') {
        errorMessage = 'Firebase is currently unavailable. Please try again later.';
      } else if (error.message) {
        errorMessage = `Error: ${error.message}`;
      }
      
      setError(errorMessage);
      setTimeout(() => setError(null), LONG_ERROR_DISPLAY_TIMEOUT);
    } finally {
      setSubmitting(false);
      clearTimeout(timeoutId);
    }
  };

  const handleEditReview = async (e) => {
    e.preventDefault();
    if (!editingReview) return;

    setSubmitting(true);
    try {
      let imageUrls = [...editingReview.images];
      
      if (selectedImages.length > 0) {
        try {
          const newImages = await uploadImages();
          imageUrls = [...imageUrls, ...newImages];
        } catch (uploadError) {
          console.warn('Image upload failed during edit, keeping existing images:', uploadError);
          setError('New images could not be uploaded, but your review will be updated. Please try uploading images later.');
          setTimeout(() => setError(null), LONG_ERROR_DISPLAY_TIMEOUT);
        }
      }

      const reviewRef = doc(db, 'reviews', editingReview.id);
      await updateDoc(reviewRef, {
        rating,
        text: reviewText,
        tags: selectedTags,
        images: imageUrls,
        updatedAt: serverTimestamp()
      });

      // Reset form
      setReviewText('');
      setRating(null);
      setSelectedTags([]);
      setSelectedImages([]);
      setShowReviewForm(false);
      setEditingReview(null);
      
      // Clean up object URLs
      cleanupObjectUrls();
    } catch (error) {
      console.error('Error updating review:', error);
      setError('Error updating review. Please try again.');
      setTimeout(() => setError(null), ERROR_DISPLAY_TIMEOUT);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteReview = async (reviewId, images) => {
    if (!user) {
      setError('You must be signed in to delete reviews.');
      setTimeout(() => setError(null), ERROR_DISPLAY_TIMEOUT);
      return;
    }

    if (!window.confirm('Are you sure you want to delete this review?')) return;

    setSubmitting(true);
    setError(null);

    try {
      // Delete images from storage first (skip in development due to CORS issues)
      const isDevelopment = process.env.NODE_ENV === 'development' || 
                           window.location.hostname === 'localhost' || 
                           window.location.hostname === '127.0.0.1';
      
      if (images && images.length > 0 && !isDevelopment) {
        const deletePromises = images.map(image => {
          if (image.path) {
            const imageRef = ref(storage, image.path);
            return deleteObject(imageRef).catch(error => {
              // Don't fail the entire operation if image deletion fails
              return Promise.resolve();
            });
          }
          return Promise.resolve();
        });
        await Promise.all(deletePromises);
      }

      // Delete review document
      await deleteDoc(doc(db, 'reviews', reviewId));
      
      // Clear any existing review state
      setUserExistingReview(null);
      
    } catch (error) {
      let errorMessage = 'Error deleting review. Please try again.';
      
      // More specific error messages based on Firebase error codes
      if (error.code === 'permission-denied') {
        errorMessage = 'Permission denied. You can only delete your own reviews.';
      } else if (error.code === 'unavailable') {
        errorMessage = 'Firebase is currently unavailable. Please try again later.';
      } else if (error.code === 'not-found') {
        errorMessage = 'Review not found. It may have already been deleted.';
      } else if (error.code === 'unauthenticated') {
        errorMessage = 'You must be signed in to delete reviews.';
      } else if (error.message) {
        errorMessage = `Error: ${error.message}`;
      }
      
      setError(errorMessage);
      setTimeout(() => setError(null), LONG_ERROR_DISPLAY_TIMEOUT);
    } finally {
      setSubmitting(false);
    }
  };

  const startEditing = (review) => {
    setEditingReview(review);
    setReviewText(review.text);
    setRating(review.rating);
    setSelectedTags(review.tags || []);
    setSelectedImages([]);
    setShowReviewForm(true);
  };

  const openReviewForm = () => {
    // Check both state and current reviews array for existing review
    const hasExistingReview = userExistingReview || reviews.find(review => review.userId === user.uid);
    
    if (hasExistingReview && !editingReview) {
      // Auto-populate form with existing review data
      const existingReview = hasExistingReview;
      setEditingReview(existingReview);
      setReviewText(existingReview.text);
      setRating(existingReview.rating);
      setSelectedTags(existingReview.tags || []);
      setSelectedImages([]);
    } else {
      // Clear form for new review
      setEditingReview(null);
      setReviewText('');
      setRating(null);
      setSelectedTags([]);
      setSelectedImages([]);
    }
    setShowReviewForm(true);
  };

  const cancelEditing = () => {
    setEditingReview(null);
    setReviewText('');
    setRating(null);
    setSelectedTags([]);
    setSelectedImages([]);
    setShowReviewForm(false);
    
    // Clean up object URLs
    cleanupObjectUrls();
    
    // Reset to show existing review if user has one
    if (userExistingReview) {
      setEditingReview(userExistingReview);
      setReviewText(userExistingReview.text);
      setRating(userExistingReview.rating);
      setSelectedTags(userExistingReview.tags || []);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => {
      const starValue = i + 1;
      const halfStarValue = i + 0.5;
      const isFullStar = rating >= starValue;
      const isHalfStar = rating >= halfStarValue && rating < starValue;
      
      return (
        <div key={i} style={{ position: 'relative', display: 'inline-block', width: '1rem', height: '1rem' }}>
          {/* Single Circle with Split Background */}
          <div
            style={{
              width: '1rem',
              height: '1rem',
              borderRadius: '50%',
              border: '2px solid #ddd',
              background: isFullStar 
                ? '#1D665D' 
                : isHalfStar 
                  ? 'linear-gradient(to right, #1D665D 50%, transparent 50%)'
                  : 'transparent',
              position: 'relative',
              zIndex: 1
            }}
          />
        </div>
      );
    });
  };

  const renderHalfStars = (rating, onStarClick, onStarHover, onStarLeave) => {
    return Array.from({ length: 5 }, (_, i) => {
      const starValue = i + 1;
      const halfStarValue = i + 0.5;
      const isFullStar = rating >= starValue;
      const isHalfStar = rating >= halfStarValue && rating < starValue;
      
      // Hover logic: show cumulative circles on hover
      const isHovered = hoverRating >= starValue;
      const isHoveredHalf = hoverRating >= halfStarValue && hoverRating < starValue;
      
      return (
        <div key={i} style={{ position: 'relative', display: 'inline-block', width: '1.5rem', height: '1.5rem' }}>
          {/* Single Circle with Split Background */}
          <div
            style={{
              width: '1.5rem',
              height: '1.5rem',
              borderRadius: '50%',
              border: '2px solid #ddd',
              background: (isFullStar || isHovered) 
                ? '#1D665D' 
                : (isHalfStar || isHoveredHalf)
                  ? 'linear-gradient(to right, #1D665D 50%, transparent 50%)'
                  : 'transparent',
              cursor: 'pointer',
              position: 'relative',
              zIndex: 2,
              transition: 'all 0.2s ease'
            }}
            onClick={() => onStarClick(starValue)}
            onMouseEnter={() => onStarHover(starValue)}
            onMouseLeave={onStarLeave}
          />
          
          {/* Invisible overlay for left half click/hover */}
          <div
            style={{
              width: '0.75rem',
              height: '1.5rem',
              position: 'absolute',
              top: '0px',
              left: '0px',
              cursor: 'pointer',
              zIndex: 3
            }}
            onClick={() => onStarClick(halfStarValue)}
            onMouseEnter={() => onStarHover(halfStarValue)}
            onMouseLeave={onStarLeave}
          />
        </div>
      );
    });
  };

  const handleTagToggle = (tagId) => {
    setSelectedTags(prev => 
      prev.includes(tagId) 
        ? prev.filter(id => id !== tagId)
        : [...prev, tagId]
    );
  };

  const calculateAverageRating = () => {
    if (reviews.length === 0) return 0;
    const totalRating = reviews.reduce((sum, review) => sum + review.rating, 0);
    return (totalRating / reviews.length).toFixed(1);
  };

  const getRatingDistribution = () => {
    const distribution = { 5: 0, 4.5: 0, 4: 0, 3.5: 0, 3: 0, 2.5: 0, 2: 0, 1.5: 0, 1: 0, 0.5: 0 };
    reviews.forEach(review => {
      const rating = review.rating;
      if (distribution.hasOwnProperty(rating)) {
        distribution[rating]++;
      }
    });
    return distribution;
  };

  return (
    <div style={{ marginTop: '2rem' }}>
             <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
         <h2 style={{ margin: 0, color: '#1f2937', fontSize: '1.875rem', fontWeight: 700 }}>
           Reviews ({reviews.length})
         </h2>
                 {user ? (
           <button
             onClick={openReviewForm}
             style={{
               background: '#1D665D',
               color: '#fff',
               border: 'none',
               borderRadius: 8,
               padding: '0.75rem 1.5rem',
               fontWeight: 600,
               fontSize: '0.875rem',
               cursor: 'pointer',
               transition: 'all 0.2s ease'
             }}
           >
             {userExistingReview ? 'Edit My Review' : 'Write a Review'}
           </button>
         ) : (
          <div style={{ textAlign: 'center' }}>
            <p style={{ color: '#64748b', fontSize: '0.875rem', margin: '0 0 0.5rem 0' }}>
              Sign in to write a review
            </p>
            <button
              onClick={() => window.location.href = '/login'}
              style={{
                background: '#1D665D',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                padding: '0.5rem 1rem',
                fontWeight: 600,
                fontSize: '0.75rem',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              Sign In
            </button>
          </div>
        )}
      </div>

      {/* Reviews Summary */}
      {reviews.length > 0 && (
        <div style={{
          background: '#f8fafc',
          borderRadius: 12,
          padding: '1.5rem',
          border: '1px solid #e2e8f0',
          marginBottom: '2rem'
        }}>
                     <div style={{ textAlign: 'center' }}>
             {/* Average Rating */}
             <div style={{ fontSize: '2.5rem', fontWeight: 700, color: '#1D665D', marginBottom: '0.5rem' }}>
               {calculateAverageRating()}
             </div>
             <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '0.5rem' }}>
               {renderStars(parseFloat(calculateAverageRating()))}
             </div>
             <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
               {reviews.length} review{reviews.length !== 1 ? 's' : ''}
             </div>
           </div>
        </div>
      )}

      {/* Review Form */}
               {showReviewForm && (
           <div style={{
             background: '#f8fafc',
             borderRadius: 12,
             padding: '1.5rem',
             border: '1px solid #e2e8f0',
             marginBottom: '2rem'
           }}>
             <h3 style={{ marginBottom: '1rem', color: '#374151' }}>
               {editingReview ? 'Edit Review' : (userExistingReview ? 'Edit My Review' : 'Write a Review')}
             </h3>
          
          <form onSubmit={editingReview ? handleEditReview : handleSubmitReview}>
            {/* Rating */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                Rating: <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                {renderHalfStars(
                  rating || 0,
                  (value) => setRating(value),
                  (value) => setHoverRating(value),
                  () => setHoverRating(null)
                )}
                <span style={{ marginLeft: '0.5rem', color: '#666' }}>
                  {rating ? `${rating} out of 5` : 'Select a rating'}
                </span>
              </div>
            </div>

            {/* Above-average */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                Above-average:
              </label>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                {availableTags.map((tag) => (
                  <button
                    key={tag.id}
                    type="button"
                    onClick={() => handleTagToggle(tag.id)}
                    style={{
                      background: selectedTags.includes(tag.id) ? '#1D665D' : '#e5e7eb',
                      color: selectedTags.includes(tag.id) ? 'white' : '#374151',
                      border: '1px solid #d1d5db',
                      borderRadius: '20px',
                      padding: '0.5rem 1rem',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {tag.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Review Text */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                Your Review:
              </label>
              <textarea
                value={reviewText}
                onChange={(e) => setReviewText(e.target.value)}
                required
                rows={4}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: 8,
                  fontSize: isMobile ? '16px' : '0.875rem',
                  resize: 'vertical'
                }}
                placeholder="Share your experience with this course..."
              />
            </div>

                         {/* Image Upload */}
             <div style={{ marginBottom: '1rem' }}>
               <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                 Add Photos (max 5):
               </label>
               
               <input
                 ref={fileInputRef}
                 type="file"
                 multiple
                 accept="image/*"
                 onChange={handleImageSelect}
                 style={{ display: 'none' }}
               />
               <button
                 type="button"
                 onClick={() => fileInputRef.current?.click()}
                 style={{
                   background: '#e5e7eb',
                   color: '#374151',
                   border: '1px solid #d1d5db',
                   borderRadius: 8,
                   padding: '0.5rem 1rem',
                   cursor: 'pointer',
                   fontSize: '0.875rem'
                 }}
               >
                 Choose Images
               </button>
              
              {/* Selected Images Preview */}
              {selectedImages.length > 0 && (
                <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  {selectedImages.map((file, index) => (
                    <ImagePreview 
                      key={`${file.name}-${index}`}
                      file={file} 
                      index={index} 
                      onRemove={removeImage} 
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Submit Buttons */}
            <div style={{ display: 'flex', gap: '1rem' }}>
                             <button
                 type="submit"
                 disabled={submitting || (userExistingReview && !editingReview)}
                 style={{
                   background: '#1D665D',
                   color: '#fff',
                   border: 'none',
                   borderRadius: 8,
                   padding: '0.75rem 1.5rem',
                   fontWeight: 600,
                   fontSize: '0.875rem',
                   cursor: (submitting || (userExistingReview && !editingReview)) ? 'not-allowed' : 'pointer',
                   opacity: (submitting || (userExistingReview && !editingReview)) ? 0.6 : 1
                 }}
               >
                 {submitting ? 'Submitting...' : (editingReview ? 'Update Review' : 'Submit Review')}
               </button>
              <button
                type="button"
                onClick={cancelEditing}
                style={{
                  background: '#e5e7eb',
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  borderRadius: 8,
                  padding: '0.75rem 1.5rem',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              {submitting && (
                <button
                  type="button"
                  onClick={() => {
                    setSubmitting(false);
                    setError('Submission cancelled. Please try again.');
                    setTimeout(() => setError(null), ERROR_DISPLAY_TIMEOUT);
                  }}
                  style={{
                    background: '#ef4444',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 8,
                    padding: '0.75rem 1.5rem',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    cursor: 'pointer'
                  }}
                >
                  Force Cancel
                </button>
              )}
            </div>
          </form>
          
          {/* Error Display */}
          {error && (
            <div style={{
              marginTop: '1rem',
              padding: '0.75rem',
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '8px',
              color: '#dc2626',
              fontSize: '0.875rem'
            }}>
              {error}
            </div>
          )}
        </div>
      )}

      {/* Reviews List */}
      {reviews.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '3rem 2rem',
          background: '#f8fafc',
          borderRadius: 12,
          border: '2px dashed #cbd5e1'
        }}>
          <p style={{ color: '#64748b', fontStyle: 'italic', fontSize: '1rem', margin: 0 }}>
            No reviews yet for this course.
          </p>
          <p style={{ color: '#94a3b8', fontSize: '0.875rem', margin: '0.5rem 0 0 0' }}>
            Be the first to share your experience!
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          {reviews.map((review) => (
            <div
              key={review.id}
              style={{
                background: '#fff',
                borderRadius: 12,
                padding: '1.5rem',
                border: '1px solid #e2e8f0',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
              }}
            >
                                            {/* Review Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                  <div>
                                         <div style={{ fontWeight: 600, color: '#374151', marginBottom: '0.25rem', display: 'flex', alignItems: 'center' }}>
                                               <a
                          href={`/user/${review.username}`}
                          style={{
                            color: '#1D665D',
                            textDecoration: 'underline',
                            cursor: 'pointer',
                            transition: 'color 0.2s ease'
                          }}
                          onMouseEnter={(e) => e.target.style.color = '#0f4c3f'}
                          onMouseLeave={(e) => e.target.style.color = '#1D665D'}
                        >
                          {review.displayName}
                        </a>
                       <UserBadges badges={userBadges[review.userId]} size="small" />
                     </div>
                                       <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <div style={{ display: 'flex' }}>
                        {renderStars(review.rating)}
                      </div>
                    </div>
                 </div>
                
                {/* Edit/Delete buttons for review owner */}
                {user && user.uid === review.userId && (
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      onClick={() => startEditing(review)}
                      style={{
                        background: '#3b82f6',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 6,
                        padding: '0.5rem 1rem',
                        fontSize: '0.75rem',
                        cursor: 'pointer'
                      }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteReview(review.id, review.images)}
                      disabled={submitting}
                      style={{
                        background: '#ef4444',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 6,
                        padding: '0.5rem 1rem',
                        fontSize: '0.75rem',
                        cursor: submitting ? 'not-allowed' : 'pointer',
                        opacity: submitting ? 0.6 : 1
                      }}
                    >
                      {submitting ? 'Deleting...' : 'Delete'}
                    </button>
                  </div>
                )}
              </div>

              {/* Review Text */}
              <div style={{ marginBottom: '1rem', lineHeight: '1.6', color: '#374151' }}>
                {review.text}
              </div>

              {/* Review Tags */}
              {review.tags && review.tags.length > 0 && (
                <div style={{ marginBottom: '1rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  {review.tags.map((tagId) => {
                    const tag = availableTags.find(t => t.id === tagId);
                    if (!tag) return null;
                    return (
                      <span
                        key={tagId}
                        style={{
                          background: '#1D665D',
                          color: 'white',
                          padding: '0.25rem 0.75rem',
                          borderRadius: '12px',
                          fontSize: '0.75rem',
                          fontWeight: 500
                        }}
                      >
                        {tag.label}
                      </span>
                    );
                  })}
                </div>
              )}

              {/* Review Images */}
              {review.images && review.images.length > 0 && (
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  {review.images.map((image, index) => (
                    <img
                      key={index}
                      src={image.url}
                      alt={`Review image ${index + 1}`}
                      style={{
                        width: isMobile ? IMAGE_DIMENSIONS.mobile : IMAGE_DIMENSIONS.desktop,
                        height: isMobile ? IMAGE_DIMENSIONS.mobile : IMAGE_DIMENSIONS.desktop,
                        objectFit: 'cover',
                        borderRadius: 8,
                        border: '1px solid #e2e8f0',
                        cursor: 'pointer'
                      }}
                      onClick={() => {
                        setSelectedImage(image.url);
                        setShowImageModal(true);
                      }}
                    />
                  ))}
                </div>
              )}

                             {/* Date indicator */}
               <div style={{ marginTop: '1rem', fontSize: '0.875rem', color: '#374151' }}>
                 {formatDate(review.createdAt)}
               </div>
            </div>
          ))}
        </div>
      )}

      {/* Image Modal */}
      <ImageModal
        isOpen={showImageModal}
        imageUrl={selectedImage}
        onClose={() => {
          setShowImageModal(false);
          setSelectedImage(null);
        }}
      />
    </div>
  );
};

export default CourseReviews;
